import {defaultRangeExtractor, type Range, useWindowVirtualizer, type Virtualizer} from '@tanstack/react-virtual'
import type React from 'react'
import {useMemo} from 'react'

import {useCurrentLineHeight} from '../../../../../hooks/use-current-line-height'

export type WindowVirtualizer = Virtualizer<Window, HTMLElement>

interface VirtualCodeBlobOptions {
  parentRef: React.RefObject<HTMLDivElement | null>
  lineCount: number
  materializeAllLines: boolean
}

interface VirtualCodeBlobResult {
  virtualizer: ReturnType<typeof useWindowVirtualizer>
  currentLineHeight: number
}

const defaultOverscanAmount = 100
const defaultHeadAndTailLength = 75

export function useVirtualCodeBlob({lineCount, materializeAllLines}: VirtualCodeBlobOptions): VirtualCodeBlobResult {
  const currentLineHeight = useCurrentLineHeight('react-line-numbers')
  const estimateSize = useMemo(() => () => currentLineHeight, [currentLineHeight])
  const rangeExtractor = useMemo(() => getRangeExtractor(lineCount), [lineCount])

  const virtualizer = useWindowVirtualizer({
    count: lineCount,
    overscan: materializeAllLines ? Number.MAX_SAFE_INTEGER : defaultOverscanAmount,
    estimateSize,
    rangeExtractor,
    measureElement: element => element.getBoundingClientRect().height,
    scrollToFn,
  })

  return {virtualizer, currentLineHeight}
}

function scrollToFn(offset: number) {
  // Use 'instant' to prevent overshoot/bounce when scrolling in virtualized view.
  // Smooth scrolling can fight with virtualization re-rendering and measurement.
  window.scrollTo({top: offset, left: 0, behavior: 'instant'})
}

export function getRangeExtractor(lineCount: number): (range: Range) => number[] {
  return function customRangeExtractor(range: Range) {
    if (lineCount < 2 * defaultHeadAndTailLength) {
      // Render all lines if the total length of the head and tail
      // together would be longer than the length of the file.
      return rangeArray(0, lineCount)
    }

    const indicesInWindow = defaultRangeExtractor(range)
    if (indicesInWindow.length === 0) {
      // This happens sometimes on the first render pass.
      // Just let the virtualization library do its thing on the next render.
      return indicesInWindow
    }

    const minIndexInWindow = indicesInWindow[0]!
    const maxIndexInWindow = indicesInWindow[indicesInWindow.length - 1]!

    const headLength = Math.min(defaultHeadAndTailLength, minIndexInWindow)
    const tailLength = Math.min(defaultHeadAndTailLength, lineCount - maxIndexInWindow)

    const head = rangeArray(0, headLength)
    const tail = rangeArray(lineCount + 1 - tailLength, lineCount)

    return head.concat(indicesInWindow, tail)
  }
}

function rangeArray(min: number, max: number) {
  return new Array(max - min).fill(null).map((_, i) => i + min)
}
